import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import useI18n from '../../../i18n/useI18n'
import Modal from '../../../components/modal'

const CommissionCard = ({ agent }) => {
    const { t } = useI18n()
    const navigate = useNavigate()
    const [isContractModalOpen, setIsContractModalOpen] = useState(false)
    const [isDealerModalOpen, setIsDealerModalOpen] = useState(false)
    const commissionData = agent?.agent ? agent : { agent }
    const agentInfo = commissionData.agent || {}

    const getAgentName = () => {
        if (agentInfo.companyName) return agentInfo.companyName
        if (agentInfo.name) return agentInfo.name
        const firstName = agentInfo?.user?.firstName || ''
        const lastName = agentInfo?.user?.lastName || ''
        const combined = `${firstName} ${lastName}`.trim()
        return combined || agentInfo?.user?.email || t('AGENTS_ENTITY')
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(date)
    }

    const logoUrl = agentInfo.logoUrl || agentInfo.logoURL || agentInfo.logo
    const contractNumber =
        commissionData.contractNumber || commissionData.contractNo || commissionData.contractId || '-'
    const commissionStatus = commissionData.status || agentInfo.contractStatus || agentInfo.status
    const licenseNumber =
        agentInfo.licenseNumber || agentInfo.licenseNo || commissionData.licenseNumber || '-'
    const licenseExpiryDate =
        agentInfo.licenseExpiryDate || commissionData.licenseExpiryDate || commissionData.licenseExpiry
    const totalStudents = commissionData.totalStudents ?? agentInfo.totalStudents ?? 0
    const totalApplications = commissionData.totalApplications ?? agentInfo.totalApplications ?? 0
    const commissionRate = commissionData.commissionRate ?? agentInfo.commissionRate ?? 0
    const contractStartDate = commissionData.contractStartDate ?? agentInfo.contractStartDate
    const contractEndDate = commissionData.contractEndDate ?? agentInfo.contractEndDate

    return (
        <div className="card commission-card">
            <div className="body">
                {/* Header Section */}
                <div className="commission-card-header">
                    <div className="d-flex align-items-center">
                        {logoUrl ? (
                            <div className="commission-logo">
                                <img
                                    src={logoUrl}
                                    alt={getAgentName()}
                                    onError={(e) => {
                                        e.target.style.display = 'none'
                                        e.target.nextSibling.style.display = 'flex'
                                    }}
                                />
                                <div className="commission-logo-placeholder" style={{ display: 'none' }}>
                                    <i className="zmdi zmdi-account-box"></i>
                                </div>
                            </div>
                        ) : (
                            <div className="commission-logo">
                                <div className="commission-logo-placeholder">
                                    <i className="zmdi zmdi-account-box"></i>
                                </div>
                            </div>
                        )}

                        <div className="commission-info flex-grow-1 ml-3">
                            <div className="commission-title-row">
                                <h5 style={{ textAlign: 'left' }} className="mb-1">{getAgentName()}</h5>
                                <button
                                    type="button"
                                    className="commission-info-button commission-info-icon"
                                    onClick={() => setIsDealerModalOpen(true)}
                                    title={t('AGENT_COMMISSIONS_INFO_BUTTON')}
                                >
                                    <i className="zmdi zmdi-info-outline"></i>
                                </button>
                            </div>
                            <div className="commission-meta">
                                <button
                                    type="button"
                                    className="badge badge-info commission-license-button mr-2"
                                    onClick={() => setIsContractModalOpen(true)}
                                >
                                    <i className="zmdi zmdi-assignment mr-1"></i>
                                    {licenseNumber}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="commission-stats mt-3">
                    <div className="row">
                        <div className="col-6">
                            <div className="stat-item">
                                <div className="stat-icon bg-blue">
                                    <i className="zmdi zmdi-accounts"></i>
                                </div>
                                <div className="stat-content">
                                    <span className="stat-label">{t('AGENT_COMMISSIONS_TOTAL_STUDENTS')}</span>
                                    <span className="stat-value">{totalStudents}</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="stat-item">
                                <div className="stat-icon bg-orange">
                                    <i className="zmdi zmdi-file"></i>
                                </div>
                                <div className="stat-content">
                                    <span className="stat-label">{t('AGENT_COMMISSIONS_APPLICATIONS')}</span>
                                    <span className="stat-value">{totalApplications}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-2">
                        <div className="col-6">
                            <div className="stat-item">
                                <div className="stat-icon bg-green">
                                    <i className="zmdi zmdi-check-circle"></i>
                                </div>
                                <div className="stat-content">
                                    <span className="stat-label">{t('AGENT_COMMISSIONS_COMMISSION_RATE')}</span>
                                    <span className="stat-value">{commissionRate || 0}%</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="stat-item">
                                <div className="stat-icon bg-purple">
                                    <i className="zmdi zmdi-calendar"></i>
                                </div>
                                <div className="stat-content">
                                    <span className="stat-label">{t('AGENT_COMMISSIONS_CONTRACT_PERIOD')}</span>
                                    <span className="stat-value text-small">
                                        {formatDate(contractStartDate)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="commission-actions mt-3">
                    <button
                    style={{display:'flex', alignItems:'center', justifyContent:'center'}}
                        type="button"
                        className="btn btn-warning  btn-block commission-detail-button"
                        onClick={() => navigate(`/agent-commissions/${agentInfo.id || agent.id}`, { state: { agent: commissionData } })}
                    >
                        <i className="zmdi zmdi-open-in-new mr-2"></i>
                        {t('AGENT_COMMISSIONS_VIEW_DETAILS')}
                    </button>
                </div>
            </div>

            <Modal
                isOpen={isContractModalOpen}
                onClose={() => setIsContractModalOpen(false)}
                title={t('AGENT_COMMISSIONS_CONTRACT_MODAL_TITLE')}
                subtitle={getAgentName()}
                icon="zmdi-file-text"
                size="medium"
                className="contract-detail-modal"
            >
                <div className="detail-grid">
                    <div className="detail-item">
                        <span className="detail-label">{t('AGENT_COMMISSIONS_CONTRACT_NUMBER')}</span>
                        <span className="detail-value">{contractNumber}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">{t('AGENT_COMMISSIONS_LICENSE_NUMBER')}</span>
                        <span className="detail-value">{licenseNumber}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">{t('AGENT_COMMISSIONS_LICENSE_EXPIRY')}</span>
                        <span className="detail-value">{formatDate(licenseExpiryDate)}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">{t('AGENT_COMMISSIONS_CONTRACT_START')}</span>
                        <span className="detail-value">{formatDate(contractStartDate)}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">{t('AGENT_COMMISSIONS_CONTRACT_END')}</span>
                        <span className="detail-value">{formatDate(contractEndDate)}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">{t('AGENT_COMMISSIONS_COMMISSION_RATE')}</span>
                        <span className="detail-value">{commissionRate || 0}%</span>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isDealerModalOpen}
                onClose={() => setIsDealerModalOpen(false)}
                title={t('AGENT_COMMISSIONS_DEALER_INFO_TITLE')}
                subtitle={getAgentName()}
                icon="zmdi-store"
                size="medium"
                className="dealer-detail-modal"
            >
                <div className="dealer-modal-content">
                    <div className="dealer-summary">
                        <div className="dealer-summary-main">
                            <span className="dealer-summary-label">{t('AGENTS_TABLE_COLUMNS_COMPANY')}</span>
                            <span className="dealer-summary-value">{agentInfo.companyName || '-'}</span>
                        </div>
                        <div className="dealer-summary-meta">
                            <span className="dealer-chip">
                                <i className="zmdi zmdi-assignment"></i>
                                {agentInfo.licenseNumber || '-'}
                            </span>
                            <span className="dealer-chip">
                                <i className="zmdi zmdi-calendar"></i>
                                {formatDate(agentInfo.licenseExpiryDate)}
                            </span>
                            <span className="dealer-chip">
                                <i className="zmdi zmdi-percent"></i>
                                {agentInfo.commissionRate || 0}%
                            </span>
                        </div>
                    </div>

                    <div className="dealer-section-card">
                        <h6 className="dealer-section-title">
                            <i className="zmdi zmdi-account"></i>
                            {t('AGENT_COMMISSIONS_CONTACT_INFO')}
                        </h6>
                        <div className="dealer-grid">
                            <div className="dealer-item">
                                <span className="dealer-label">{t('AGENTS_TABLE_COLUMNS_EMAIL')}</span>
                                <span className="dealer-value">{agentInfo.user?.email || agentInfo.email || '-'}</span>
                            </div>
                            <div className="dealer-item">
                                <span className="dealer-label">{t('AGENTS_TABLE_COLUMNS_PHONE')}</span>
                                <span className="dealer-value">{agentInfo.user?.phone || agentInfo.phone || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="dealer-section-card">
                        <h6 className="dealer-section-title">
                            <i className="zmdi zmdi-pin"></i>
                            {t('AGENTS_TABLE_COLUMNS_ADDRESS')}
                        </h6>
                        <div className="dealer-grid">
                            <div className="dealer-item dealer-full">
                                <span className="dealer-label">{t('AGENTS_TABLE_COLUMNS_ADDRESS')}</span>
                                <span className="dealer-value">{agentInfo.address || agentInfo.user?.address || '-'}</span>
                            </div>
                            <div className="dealer-item">
                                <span className="dealer-label">{t('AGENTS_TABLE_COLUMNS_CITY')}</span>
                                <span className="dealer-value">{agentInfo.city || '-'}</span>
                            </div>
                            <div className="dealer-item">
                                <span className="dealer-label">{t('AGENTS_TABLE_COLUMNS_COUNTRY')}</span>
                                <span className="dealer-value">{agentInfo.country || '-'}</span>
                            </div>
                        </div>
                    </div>

                </div>
            </Modal>
        </div>
    )
}

CommissionCard.propTypes = {
    agent: PropTypes.object.isRequired
}

export default CommissionCard
